/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-28 16:07:55
 *
 *******************************************************/
// @fow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_MERGE_VALUES_TYPES_REQUEST,
  GET_MERGE_VALUES_TYPES_SUCCESS,
  GET_MERGE_VALUES_TYPES_FAILURE,
  DELETE_MERGE_VALUES_TYPE_REQUEST,
  DELETE_MERGE_VALUES_TYPE_SUCCESS,
  DELETE_MERGE_VALUES_TYPE_FAILURE,
  UPDATE_MERGE_VALUES_TYPE_STATUS_REQUEST,
  UPDATE_MERGE_VALUES_TYPE_STATUS_SUCCESS,
  UPDATE_MERGE_VALUES_TYPE_STATUS_FAILURE,
  GET_MERGE_VALUES_BY_TYPE_REQUEST,
  GET_MERGE_VALUES_BY_TYPE_SUCCESS,
  GET_MERGE_VALUES_BY_TYPE_FAILURE,
  SAVE_MERGE_VALUES_RULE_REQUEST,
  SAVE_MERGE_VALUES_RULE_SUCCESS,
  SAVE_MERGE_VALUES_RULE_FAILURE,
  DELETE_MERGE_VALUES_RULE_REQUEST,
  DELETE_MERGE_VALUES_RULE_SUCCESS,
  DELETE_MERGE_VALUES_RULE_FAILURE,
  UPDATE_MERGE_VALUES_RULE_STATUS_REQUEST,
  UPDATE_MERGE_VALUES_RULE_STATUS_SUCCESS,
  UPDATE_MERGE_VALUES_RULE_STATUS_FAILURE,
  RESET_MERGE_VALUE_ITEM
} from 'stores/flow-type/merge-values.type';
import { MergeValuesRulesByTypeType } from 'src/models/merge-values';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get list of type of merge values from  api
 * @return {object} The RSAA object for API middleware
 */
export const getMergeValuesTypes = (i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merge-values.get-merge-values-types', {}, [
    GET_MERGE_VALUES_TYPES_REQUEST,
    {
      type: GET_MERGE_VALUES_TYPES_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_MERGE_VALUES_TYPES_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MergeValues.Actions.getMergeValuesTypes'),
          i18n
        )
    }
  ]);
};

/**
 * call to delete merge values type api
 * @return {object} The RSAA object for API middleware
 */
export const deleteMergeValuesType = (type: string, i18n: I18n) => {
  const params = { type };
  return apiService.sendRequest('admin-v3.merge-values.delete-merge-values-type', { params }, [
    DELETE_MERGE_VALUES_TYPE_REQUEST,
    {
      type: DELETE_MERGE_VALUES_TYPE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.MergeValues.Actions.deleteMergeValuesType_Success'),
          i18n
        );
        // Track Full Story event
        trackFSEvent('Delete Merge Value', {
          feature: 'Merge Values',
          mergeType: type
        });

        return { data, type };
      }
    },
    {
      type: DELETE_MERGE_VALUES_TYPE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MergeValues.Actions.deleteMergeValuesType_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to update merge values type status api
 * @return {object} The RSAA object for API middleware
 */
export const updateMergeValuesTypeStatus = (type: string, status: string, i18n: I18n) => {
  const body = { status };
  const params = { type };
  return apiService.sendRequest(
    'admin-v3.merge-values.update-merge-values-type-status',
    { params, body },
    [
      { type: UPDATE_MERGE_VALUES_TYPE_STATUS_REQUEST, payload: { type, status } },
      {
        type: UPDATE_MERGE_VALUES_TYPE_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom-with-sync',
            i18n.translate('AdminHTML.MergeValues.Actions.updateMergeValuesTypeStatus_Success'),
            i18n
          );
          //Tracking event
          if (status === 'active') {
            trackFSEvent('Turn On Merge Value', {
              feature: 'Merge Values',
              mergeType: type
            });
          }
          if (status === 'disabled') {
            trackFSEvent('Turn Off Merge Value', {
              feature: 'Merge Values',
              mergeType: type
            });
          }

          return { data, type, status };
        }
      },
      {
        type: UPDATE_MERGE_VALUES_TYPE_STATUS_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.MergeValues.Actions.updateMergeValuesTypeStatus_Failure'),
            i18n
          );
          return { data, type, status };
        }
      }
    ]
  );
};

/**
 * call to get merge values by name from  api
 * @return {object} The RSAA object for API middleware
 */
export const getMergeValuesByType = (type: string, i18n: I18n) => {
  const params = { type };
  return apiService.sendRequest('admin-v3.merge-values.get-merge-values-by-type', { params }, [
    GET_MERGE_VALUES_BY_TYPE_REQUEST,
    {
      type: GET_MERGE_VALUES_BY_TYPE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_MERGE_VALUES_BY_TYPE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MergeValues.Actions.getMergeValuesByType'),
          i18n
        )
    }
  ]);
};

/**
 * call to save merge values rule api
 * @return {object} The RSAA object for API middleware
 */
export const saveMergeValuesRule = (data: MergeValuesRulesByTypeType, i18n: I18n) => {
  const { id, type } = data;
  const params = { type, id };
  const requestUrl = _.isEmpty(id)
    ? 'admin-v3.merge-values.create-merge-values-rule'
    : 'admin-v3.merge-values.update-merge-values-rule';
  return apiService.sendRequest(requestUrl, { body: data, params }, [
    SAVE_MERGE_VALUES_RULE_REQUEST,
    {
      type: SAVE_MERGE_VALUES_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.MergeValues.Actions.saveMergeValuesRule_Success'),
          i18n
        );
        //Tracking event
        if (!id) {
          trackFSEvent('Add Merge Value', {
            feature: 'Merge Values',
            mergeType: data.data?.type
          });
        } else {
          trackFSEvent('Edit Merge Value', {
            feature: 'Merge Values',
            mergeType: data.data?.type
          });
        }

        return { data, id };
      }
    },
    {
      type: SAVE_MERGE_VALUES_RULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MergeValues.Actions.saveMergeValuesRule_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to delete merge values rule api
 * @return {object} The RSAA object for API middleware
 */
export const deleteMergeValuesRule = (type: string, id: string, i18n: I18n) => {
  const params = { id, type };
  return apiService.sendRequest('admin-v3.merge-values.delete-merge-values-rule', { params }, [
    DELETE_MERGE_VALUES_RULE_REQUEST,
    {
      type: DELETE_MERGE_VALUES_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.MergeValues.Actions.deleteMergeValuesRule_Success'),
          i18n
        );

        // Track Full Story event
        trackFSEvent('Delete Sub Merge Value', {
          feature: 'Merge Values',
          mergeType: type
        });
        return { data, id };
      }
    },
    {
      type: DELETE_MERGE_VALUES_RULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MergeValues.Actions.deleteMergeValuesRule_Failure'),
          i18n
        )
    }
  ]);
};

/**
 * call to update merge values rule status api
 * @return {object} The RSAA object for API middleware
 */
export const updateMergeValuesRuleStatus = (
  type: string,
  id: string,
  status: string,
  i18n: I18n
) => {
  const body = { status };
  const params = { id, type };
  return apiService.sendRequest(
    'admin-v3.merge-values.update-merge-values-rule',
    { params, body },
    [
      { type: UPDATE_MERGE_VALUES_RULE_STATUS_REQUEST, payload: { id, status } },
      {
        type: UPDATE_MERGE_VALUES_RULE_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom-with-sync',
            i18n.translate('AdminHTML.MergeValues.Actions.updateMergeValuesRuleStatus_Success'),
            i18n
          );
          // Tracking event
          if (status === 'active') {
            trackFSEvent('Turn On Sub Merge Value', {
              feature: 'Merge Values',
              mergeType: type
            });
          }
          if (status === 'disabled') {
            trackFSEvent('Turn Off Sub Merge Value', {
              feature: 'Merge Values',
              mergeType: type
            });
          }

          return { data, id, status };
        }
      },
      {
        type: UPDATE_MERGE_VALUES_RULE_STATUS_FAILURE,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.MergeValues.Actions.updateMergeValuesRuleStatus_Failure'),
            i18n
          );
          return { data, id, status };
        }
      }
    ]
  );
};

/**
 * call to reset merge value item
 * @return {object} The RSAA object for API middleware
 */
export const resetMergeValuesItem = () => {
  return {
    type: RESET_MERGE_VALUE_ITEM
  };
};
